import React, { useContext, useEffect, useRef, useState } from "react";

import ApplicationContext from "../../../context/ApplicationContext";

import "react-simple-keyboard/build/css/index.css";
import {
  defaultSnookerTable,
  ISnookerTable,
} from "../../../types/ISnookerTable";
import {
  getSettings,
  getSnookerTables,
  saveReceiptRows,
  startSnookerTable,
} from "../../../services/Service";
import {
  Button,
  Card,
  CardContent,
  Container,
  Icon,
  Input,
  Menu,
  MenuItem,
  MenuMenu,
  Segment,
  Table,
} from "semantic-ui-react";
import { ITableStatusProps } from "./ITableStatusProps";
import { Epos } from "../epos";
import { AdvancePaymentModal } from "../snooker/AdvancePaymentModal";
import { IAdvancePaymentModalProps } from "../snooker/IAdvancePaymentModalProps";
import { EndTableModal } from "../snooker/EndTableModal";
import { IEndTableModalProps } from "../snooker/IEndTableModalProps";
import { IReceiptRow } from "../../../types/IReceiptRow";
import {
  formatDateTime,
  remainingTime,
  totalDuration,
  totalHireTable,
  totalSnookerTable,
} from "../../helpers/Helpers";
import { ICustomRow } from "../../../types/ICustomRow";
import { ReminderModal } from "../snooker/ReminderModal";
import { IReminderModalProps } from "../snooker/IReminderModalProps";
import { CancelTableModal } from "./modals/CancelTableModal";
import { SaveRowsModal } from "../../receiptManager/dialogs/SaveRowsModal";
import {
  IStartTimeModalProps,
  StartTimeModal,
} from "../snooker/StartTimeModal";
import { generateGUID } from "../../../helpers/Helper";
import { IProduct } from "../../../types/IProduct";

export const TableStatus: React.FC<ITableStatusProps> = (
  props: ITableStatusProps
): JSX.Element => {
  const [selectedTable, setSelectedTable] = React.useState(defaultSnookerTable);
  const [showTableStatus, setShowTableStatus] = React.useState(true);
  const [showEpos, setShowEpos] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [showEndTable, setShowEndTable] = React.useState(false);
  const [showAdvPaymentModal, setShowAdvPaymentModal] = React.useState(false);
  const [showReminder, setShowReminder] = React.useState(false);
  const [cancelTable, setCancelTable] = React.useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [startModal, setStartModal] = useState(false);

  const context = useContext(ApplicationContext);

  const [width, setWidth] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    updateTable();
    resetViews();

    addTableReceiptRows();
  }, [props.table]);

  useEffect(() => {
    /* @ts-ignore */
    setWidth(elementRef.current.getBoundingClientRect().width);
  }, []); //empty dependency array so it only runs once at render

  function addTableReceiptRows() {
    context.Receipt.setRows([]);

    const tableRows: IReceiptRow[] = JSON.parse(props.table.receiptData);

    if (tableRows === null || tableRows === undefined) {
      return;
    }

    tableRows.forEach((element) => {
      context.Receipt.addRow(element);
    });
  }

  function resetViews() {
    clear();
    setShowTableStatus(true);
  }

  function clear() {
    setShowTableStatus(false);
    setShowEpos(false);
  }

  async function update(silent: boolean) {
    if (!silent) {
      setLoading(true);
    }

    const response = await getSnookerTables(
      context.AuthenticatedUser.getToken()
    );

    if (!response) {
      context.ApplicationError.setHasError(true);
      return;
    }

    const found = response.find((t) => t.uuid === selectedTable.uuid);

    if (found !== undefined) {
      setSelectedTable(found);
    }

    context.Snooker.setData(response);

    if (!silent) {
      setLoading(false);
    }
  }

  async function updateTable() {
    setLoading(true);
    const response = await getSnookerTables(
      context.AuthenticatedUser.getToken()
    );

    if (!response) {
      context.ApplicationError.setHasError(true);
      return;
    }

    const table = response.find((t) => t.uuid === props.table.uuid);

    if (table !== undefined) {
      setSelectedTable(table);
    }

    setLoading(false);
  }

  const common = (row: IReceiptRow): JSX.Element => {
    return (
      <>
        {row.commonOptions.map((data) => (
          <>
            <br />
            &emsp;&emsp;{data.description}
            {data.price > 0 ? " (£" + data.price.toFixed(2) + ")" : <></>}
          </>
        ))}
      </>
    );
  };

  const comments = (row: IReceiptRow): JSX.Element => {
    return (
      <>
        <br />
        &emsp;&emsp;{row.notes}
      </>
    );
  };

  function deleteRow(row: IReceiptRow) {
    context.Receipt.delete(row.uuid);
    syncReceipt();
  }

  async function syncReceipt() {
    setSaving(true);
    const rows = context.Receipt.getRows();
    const rowsJson = JSON.stringify(rows);

    const isSaved = await saveReceiptRows(
      context.Snooker.getSelected().uuid,
      context.AuthenticatedUser.getToken(),
      rowsJson
    );

    if (!isSaved) {
      setError(true);
      return;
    }
    setSaving(false);
  }

  const status = (): JSX.Element => {
    return (
      <>
        <Container>
          <div>
            <div ref={elementRef}>
              <div>
                <Button primary size="huge" onClick={() => setStartModal(true)}>
                  Start
                </Button>
                <Button
                  primary
                  size="huge"
                  onClick={() => {
                    clear();
                    setShowEpos(true);
                  }}
                >
                  Menu
                </Button>
                <Button
                  primary
                  size="huge"
                  onClick={() => {
                    finishTable();
                  }}
                >
                  End
                </Button>
                <Button
                  primary
                  size="huge"
                  onClick={() => {
                    setShowAdvPaymentModal(true);
                  }}
                >
                  Adv Pay
                </Button>
                <Button
                  primary
                  size="huge"
                  onClick={() => {
                    setShowReminder(true);
                  }}
                >
                  Reminder
                </Button>
                <Button primary size="huge" onClick={() => props.close()}>
                  All Tables
                </Button>
              </div>
              <hr />

              <div style={{ padding: 10 }}>
                <Card centered style={{ width: "100%" }}>
                  <CardContent header="Started" />
                  <CardContent
                    description={
                      selectedTable.hasStarted ? (
                        formatDateTime(selectedTable.startTime)
                      ) : (
                        <>Not Started</>
                      )
                    }
                  />
                </Card>
              </div>

              <div style={{ padding: 10 }}>
                <Card centered style={{ width: "100%" }}>
                  <CardContent header="Table order" />
                  <CardContent>
                    <Table selectable>
                      <Table.Body>
                        {context.Receipt.getRows().map((data) => (
                          <Table.Row>
                            <Table.Cell width={1}>
                              <button
                                type="button"
                                className="btn btn-link"
                                style={{ width: "20px" }}
                              >
                                {data.qty}
                              </button>
                            </Table.Cell>

                            <Table.Cell>
                              {data.product.description}
                              {options(data)}
                              {instructions(data)}
                              {common(data)}
                              {comments(data)}
                            </Table.Cell>
                            <Table.Cell width={1}>
                              £{(data.qty * data.price).toFixed(2)}
                            </Table.Cell>
                            <Table.Cell width={1}>
                              {saving ? (
                                <Button basic loading>
                                  Loading
                                </Button>
                              ) : (
                                <Button basic onClick={() => deleteRow(data)}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    className="bi bi-trash"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                                  </svg>
                                </Button>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </CardContent>
                  <CardContent extra>
                    <h2>£ {getTotal().toFixed(2)}</h2>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Container>
      </>
    );
  };

  async function finishTable() {
    const advPayRow = addAdvancePaymentRow();
    const hireRow = addHireToReceiptRow();

    if (advPayRow !== undefined) {
      context.Receipt.addRow(advPayRow);
    }
    context.Receipt.addRow(hireRow);

    setShowEpos(true);
    setShowTableStatus(false);
  }

  function addAdvancePaymentRow() {
    if (props.table.advancePayment < 1) {
      return;
    }

    const defaultProduct = context.BranchMenu.getMenu().products[0];
    let product: IProduct = JSON.parse(JSON.stringify(defaultProduct));

    product.description = "Advance payment";
    product.price = props.table.advancePayment * -1;
    product.qty = 1;
    product.id = -1;

    const receiptRow: IReceiptRow = {
      id: context.Receipt.getRows().length + 1,
      product: product,
      variations: [],
      extras: [],
      commonOptions: [],
      notes: "",
      instructions: [],
      description: ["Advance Payment"],
      price: product.price,
      qty: 1,
      isDiscount: false,
      options: [],
      uuid: generateGUID(),
    };

    return receiptRow;
  }

  function addHireToReceiptRow() {
    const defaultProduct = context.BranchMenu.getMenu().products[0];
    let product: IProduct = JSON.parse(JSON.stringify(defaultProduct));

    product.description = props.table.description;
    product.price = totalHireTable(props.table);
    product.qty = 1;
    product.id = -1;

    const notes =
      formatDateTime(props.table.startTime) +
      " -> " +
      formatDateTime(new Date()) +
      " | Duration : " +
      totalDuration(props.table);

    const receiptRow: IReceiptRow = {
      id: context.Receipt.getRows().length + 1,
      product: product,
      variations: [],
      extras: [],
      commonOptions: [],
      notes: notes,
      instructions: [],
      description: [props.table.description],
      price: product.price,
      qty: 1,
      isDiscount: false,
      options: [],
      uuid: generateGUID(),
    };

    return receiptRow;
  }

  function getTotal() {
    let total = 0;
    context.Receipt.getRows().forEach((r) => {
      total += r.qty * r.price;
    });
    return total;
  }

  const options = (row: IReceiptRow): JSX.Element => {
    return (
      <>
        {row.options.map((data) => (
          <>
            {data.description === "" ? (
              <></>
            ) : (
              <>
                <br />
                &emsp;{data.description}
                {data.price > 0 ? " (£" + data.price.toFixed(2) + ")" : <></>}
              </>
            )}

            {row.variations.map((d) =>
              d.optionId === data.id ? (
                <>
                  <br />
                  &emsp;&emsp;{d.description}
                  {d.price > 0 ? " (£" + d.price.toFixed(2) + ")" : <></>}
                </>
              ) : (
                <></>
              )
            )}
            {row.extras.map((d) =>
              d.optionId === data.id ? (
                <>
                  <br />
                  &emsp;&emsp;{d.description}
                  {d.price > 0 ? " (£" + d.price.toFixed(2) + ")" : <></>}
                </>
              ) : (
                <></>
              )
            )}
          </>
        ))}
      </>
    );
  };

  const instructions = (row: IReceiptRow): JSX.Element => {
    return (
      <>
        {row.instructions.map((data) => (
          <>
            <br />
            &emsp;&emsp;{data.description}
            {data.price > 0 ? " (£" + data.price.toFixed(2) + ")" : <></>}
          </>
        ))}
      </>
    );
  };

  const tableView = (): JSX.Element => {
    return (
      <>
        {/* <Container> */}
        <Segment inverted>
          <Menu inverted secondary>
            <MenuItem name="All Tables" onClick={() => props.close()} />
            <MenuItem
              name={selectedTable.description}
              onClick={() => {
                clear();
                setShowTableStatus(true);
              }}
              active={showTableStatus}

              // active={allTablesView}
              // active={activeItem === "messages"}
              // onClick={this.handleItemClick}
            />
            <MenuItem
              name="Menu"
              active={showEpos}
              onClick={() => {
                clear();
                setShowEpos(true);
              }}
            />
          </Menu>
        </Segment>
        {showEpos ? <Epos showAllTables={showTables} /> : <></>}
        {showTableStatus ? status() : <></>}
      </>
    );
  };

  function showTables() {
    props.close();
  }

  const advancePaymentModalProps: IAdvancePaymentModalProps = {
    close: () => {
      props.updateTables();
      update(false);
      setShowAdvPaymentModal(false);
    },
    table: selectedTable,
    show: showAdvPaymentModal,
  };

  const endTableModal: IEndTableModalProps = {
    close: () => {
      props.updateTables();
      setShowEndTable(false);
    },
    endTable: () => {
      setShowEndTable(false);
      setShowEpos(true);
      setShowTableStatus(false);
    },
    table: selectedTable,
    show: showEndTable,
  };

  const reminderModal: IReminderModalProps = {
    close: () => {
      props.updateTables();
      update(false);
      setShowReminder(false);
    },
    table: selectedTable,
    show: showReminder,
  };

  const cancelModalProps: IReminderModalProps = {
    close: () => {
      props.updateTables();
      update(false);
      setCancelTable(false);
    },
    table: selectedTable,
    show: cancelTable,
  };

  const startTimeModalProps: IStartTimeModalProps = {
    close: () => {
      props.updateTables();
      update(false);
      setStartModal(false);
    },
    table: selectedTable,
    show: startModal,
  };

  return (
    <>
      <>{tableView()}</>
      <>
        <AdvancePaymentModal {...advancePaymentModalProps} />
        <EndTableModal {...endTableModal} />
        <ReminderModal {...reminderModal} />
        <CancelTableModal {...cancelModalProps} />
        <StartTimeModal {...startTimeModalProps} />
      </>
    </>
  );
};
